import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import { MASTER_HOME } from "../../../helpers/masterUser";
import MasterSpinner from "../../../components/Master/MasterSpinner";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconX() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="18" y1="6" x2="6" y2="18" />
			<line x1="6" y1="6" x2="18" y2="18" />
		</svg>
	);
}

function levelBadgeClass(level) {
	if (level === "error") return "bg-danger/15 text-danger";
	return "bg-warning/15 text-warning";
}

function formatDateTime(value) {
	if (!value) return "—";
	return new Date(value).toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
}

function truncateMessage(msg, max = 120) {
	if (!msg) return "—";
	const s = String(msg);
	return s.length <= max ? s : `${s.slice(0, max)}…`;
}

function TraceSteps({ metadata }) {
	if (!metadata?.trace || !Array.isArray(metadata.trace) || metadata.trace.length === 0) {
		return null;
	}
	return (
		<div className="mt-3 rounded-md border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900/40">
			<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-default-500">
				{i18n.t("master.scheduleLogs.traceTitle")}
			</p>
			<ol className="m-0 list-none space-y-2 p-0">
				{metadata.trace.map((step, idx) => (
					<li key={idx} className="flex gap-3 text-sm">
						<span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
							{idx + 1}
						</span>
						<div className="min-w-0 flex-1">
							<span className="font-medium text-default-800 dark:text-default-100">
								{step.step || "—"}
							</span>
							{step.at && (
								<span className="ml-2 text-xs text-default-500">{formatDateTime(step.at)}</span>
							)}
							{step.error && (
								<p className="m-0 mt-1 text-xs text-danger">{step.error}</p>
							)}
							{step.channel && (
								<p className="m-0 mt-0.5 text-xs text-default-500">
									{i18n.t("master.scheduleLogs.channel")}: {step.channel}
								</p>
							)}
							{step.reason && (
								<p className="m-0 mt-0.5 text-xs text-default-500">{step.reason}</p>
							)}
						</div>
					</li>
				))}
			</ol>
		</div>
	);
}

function TrackDrawer({ scheduleId, onClose }) {
	const [loading, setLoading] = useState(true);
	const [track, setTrack] = useState(null);

	useEffect(() => {
		if (!scheduleId) return;
		setLoading(true);
		api
			.get(`/master/schedule-logs/${scheduleId}/track`)
			.then(({ data }) => setTrack(data))
			.catch((err) => {
				toastError(err);
				onClose();
			})
			.finally(() => setLoading(false));
	}, [scheduleId, onClose]);

	if (!scheduleId) return null;

	return (
		<>
			<div
				className="fixed inset-0 z-40 bg-black/40"
				role="presentation"
				onClick={onClose}
				onKeyDown={(e) => e.key === "Escape" && onClose()}
			/>
			<aside
				className="fixed inset-y-0 end-0 z-50 flex w-full max-w-lg flex-col border-s border-default-200 bg-card shadow-xl dark:border-default-700"
				aria-label={i18n.t("master.scheduleLogs.trackTitle")}
			>
				<div className="flex items-center justify-between border-b border-default-200 px-5 py-4 dark:border-default-700">
					<div>
						<h2 className="m-0 text-lg font-semibold text-default-800 dark:text-default-100">
							{i18n.t("master.scheduleLogs.trackTitle")}
						</h2>
						<p className="m-0 mt-1 text-sm text-default-500">
							{i18n.t("master.scheduleLogs.scheduleId")} #{scheduleId}
						</p>
					</div>
					<button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label={i18n.t("master.scheduleLogs.close")}>
						<IconX />
					</button>
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto p-5">
					{loading ? (
						<div className="flex justify-center py-12">
							<MasterSpinner />
						</div>
					) : !track ? null : (
						<>
							<div className="mb-6 rounded-lg border border-default-200 p-4 dark:border-default-700">
								<dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
									<dt className="text-default-500">{i18n.t("master.scheduleLogs.colCompany")}</dt>
									<dd className="m-0 font-medium">{track.schedule?.companyName || track.schedule?.companyId || "—"}</dd>
									<dt className="text-default-500">{i18n.t("master.scheduleLogs.colStatus")}</dt>
									<dd className="m-0 font-medium">{track.schedule?.status || "—"}</dd>
									<dt className="text-default-500">{i18n.t("master.scheduleLogs.sendAt")}</dt>
									<dd className="m-0">{formatDateTime(track.schedule?.sendAt)}</dd>
									<dt className="text-default-500">{i18n.t("master.scheduleLogs.contactId")}</dt>
									<dd className="m-0">{track.schedule?.contactId ?? "—"}</dd>
									{track.summary?.lastError && (
										<>
											<dt className="col-span-2 text-default-500">{i18n.t("master.scheduleLogs.lastError")}</dt>
											<dd className="col-span-2 m-0 text-danger">{track.summary.lastError}</dd>
										</>
									)}
								</dl>
							</div>
							{track.logs?.length === 0 ? (
								<p className="text-center text-sm text-default-500">{i18n.t("master.scheduleLogs.noLogsForSchedule")}</p>
							) : (
								<ul className="m-0 list-none space-y-4 p-0">
									{track.logs.map((log) => (
										<li
											key={log.id}
											className="relative border-s-2 border-default-300 ps-4 dark:border-default-600"
										>
											<span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${levelBadgeClass(log.level)}`}>
												{log.level}
											</span>
											<span className="ml-2 text-xs font-mono text-default-500">{log.event}</span>
											<p className="m-0 mt-1 text-xs text-default-500">{formatDateTime(log.createdAt)}</p>
											<p className="m-0 mt-2 whitespace-pre-wrap break-words text-sm text-default-800 dark:text-default-100">
												{log.message}
											</p>
											<TraceSteps metadata={log.metadata} />
											{log.metadata && !log.metadata?.trace && (
												<pre className="mt-2 max-h-40 overflow-auto rounded bg-default-100 p-2 text-xs dark:bg-default-900">
													{JSON.stringify(log.metadata, null, 2)}
												</pre>
											)}
										</li>
									))}
								</ul>
							)}
						</>
					)}
				</div>
			</aside>
		</>
	);
}

export default function ScheduleLogs() {
	const history = useHistory();
	const { scheduleId: scheduleIdParam } = useParams();
	const { user, loading: authLoading } = useContext(AuthContext);

	const [companies, setCompanies] = useState([]);
	const [logs, setLogs] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [companyId, setCompanyId] = useState("");
	const [filterScheduleId, setFilterScheduleId] = useState("");
	const [level, setLevel] = useState("");
	const [eventFilter, setEventFilter] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	const trackScheduleId = scheduleIdParam ? Number(scheduleIdParam) : null;
	const routeMeta = getMasterRouteMeta(
		trackScheduleId
			? `${MASTER_HOME}/agendamentos/logs/${trackScheduleId}`
			: `${MASTER_HOME}/agendamentos/logs`
	);

	const fetchLogs = useCallback(() => {
		setLoading(true);
		const params = { pageNumber: String(page) };
		if (companyId) params.companyId = companyId;
		if (filterScheduleId) params.scheduleId = filterScheduleId;
		if (level) params.level = level;
		if (eventFilter) params.event = eventFilter;
		if (dateFrom) params.dateFrom = dateFrom;
		if (dateTo) params.dateTo = dateTo;
		if (debouncedSearch) params.search = debouncedSearch;

		api
			.get("/master/schedule-logs", { params })
			.then(({ data }) => {
				setLogs(data.logs || []);
				setTotal(data.count || 0);
			})
			.catch((err) => toastError(err))
			.finally(() => setLoading(false));
	}, [page, companyId, filterScheduleId, level, eventFilter, dateFrom, dateTo, debouncedSearch]);

	useEffect(() => {
		if (authLoading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace(MASTER_HOME);
			return;
		}
		api
			.get("/master/companies")
			.then(({ data }) => setCompanies(data || []))
			.catch((err) => toastError(err));
	}, [user, authLoading, history]);

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [search]);

	useEffect(() => {
		setPage(1);
	}, [companyId, filterScheduleId, level, eventFilter, dateFrom, dateTo, debouncedSearch]);

	useEffect(() => {
		if (authLoading || !user || user.profile !== ROLES.master) return;
		fetchLogs();
	}, [fetchLogs, authLoading, user]);

	const openTrack = (id) => {
		history.push(`${MASTER_HOME}/agendamentos/logs/${id}`);
	};

	const closeTrack = () => {
		history.push(`${MASTER_HOME}/agendamentos/logs`);
	};

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
	const rangeEnd = Math.min(page * PAGE_SIZE, total);

	if (authLoading || !user || user.profile !== ROLES.master) {
		return null;
	}

	return (
		<div className="mx-auto w-full max-w-none box-border">
			<MasterPageBreadcrumb title={i18n.t(routeMeta.titleKey)} breadcrumbs={routeMeta.breadcrumbs} />

			<div className="card">
				<div className="card-header">
					<h6 className="card-title m-0">{i18n.t("master.scheduleLogs.listTitle")}</h6>
					<span className="text-sm text-default-500">
						{total} {i18n.t("master.scheduleLogs.records")}
					</span>
				</div>

				<div className="card-body border-b border-default-200 pb-6 dark:border-default-700">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						<select
							className="form-select form-input-sm"
							value={companyId}
							onChange={(e) => setCompanyId(e.target.value)}
						>
							<option value="">{i18n.t("master.scheduleLogs.allCompanies")}</option>
							{companies.map((c) => (
								<option key={c.id} value={c.id}>
									{c.name} (#{c.id})
								</option>
							))}
						</select>
						<input
							type="number"
							className="form-input form-input-sm"
							placeholder={i18n.t("master.scheduleLogs.filterScheduleId")}
							value={filterScheduleId}
							onChange={(e) => setFilterScheduleId(e.target.value)}
						/>
						<select
							className="form-select form-input-sm"
							value={level}
							onChange={(e) => setLevel(e.target.value)}
						>
							<option value="">{i18n.t("master.scheduleLogs.allLevels")}</option>
							<option value="error">{i18n.t("master.scheduleLogs.levelError")}</option>
							<option value="warn">{i18n.t("master.scheduleLogs.levelWarn")}</option>
						</select>
						<select
							className="form-select form-input-sm"
							value={eventFilter}
							onChange={(e) => setEventFilter(e.target.value)}
						>
							<option value="">{i18n.t("master.scheduleLogs.allEvents")}</option>
							<option value="enqueue_failed">enqueue_failed</option>
							<option value="reenqueue_failed">reenqueue_failed</option>
							<option value="send_failed">send_failed</option>
							<option value="send_retry">send_retry</option>
							<option value="worker_skipped">worker_skipped</option>
						</select>
						<input type="date" className="form-input form-input-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
						<input type="date" className="form-input form-input-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
						<div className="relative sm:col-span-2 lg:col-span-1">
							<input
								type="search"
								className="form-input form-input-sm w-full ps-9"
								placeholder={i18n.t("master.scheduleLogs.searchMessage")}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
								<IconSearch />
							</div>
						</div>
					</div>
				</div>

				{loading ? (
					<div className="flex flex-col items-center justify-center gap-4 px-6 pb-8 pt-6 text-default-500">
						<MasterSpinner />
					</div>
				) : logs.length === 0 ? (
					<div className="px-6 pb-8 pt-6 text-center text-sm text-default-500">
						{i18n.t("master.scheduleLogs.empty")}
					</div>
				) : (
					<div className="overflow-x-auto pt-2">
						<table className="w-full table-auto divide-y divide-default-200 dark:divide-default-700">
							<thead className="bg-default-150 dark:bg-default-800/60">
								<tr className="text-sm font-normal text-default-700 dark:text-default-300">
									<th className="px-3.5 py-3 text-start">{i18n.t("master.scheduleLogs.colDate")}</th>
									<th className="px-3.5 py-3 text-start">{i18n.t("master.scheduleLogs.colSchedule")}</th>
									<th className="px-3.5 py-3 text-start">{i18n.t("master.scheduleLogs.colCompany")}</th>
									<th className="px-3.5 py-3 text-start">{i18n.t("master.scheduleLogs.colEvent")}</th>
									<th className="px-3.5 py-3 text-start">{i18n.t("master.scheduleLogs.colLevel")}</th>
									<th className="px-3.5 py-3 text-start">{i18n.t("master.scheduleLogs.colMessage")}</th>
									<th className="px-3.5 py-3 text-start">{i18n.t("master.scheduleLogs.colActions")}</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-default-200 dark:divide-default-700">
								{logs.map((row) => (
									<tr key={row.id} className="text-sm text-default-800 dark:text-default-200">
										<td className="whitespace-nowrap px-3.5 py-3">{formatDateTime(row.createdAt)}</td>
										<td className="px-3.5 py-3 font-mono">#{row.scheduleId}</td>
										<td className="px-3.5 py-3">{row.companyName || row.companyId}</td>
										<td className="px-3.5 py-3 font-mono text-xs">{row.event}</td>
										<td className="px-3.5 py-3">
											<span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${levelBadgeClass(row.level)}`}>
												{row.level}
											</span>
										</td>
										<td className="max-w-md px-3.5 py-3" title={row.message}>
											{truncateMessage(row.message)}
										</td>
										<td className="px-3.5 py-3">
											<button
												type="button"
												className="btn btn-sm btn-soft-primary"
												onClick={() => openTrack(row.scheduleId)}
											>
												{i18n.t("master.scheduleLogs.viewTrack")}
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{total > 0 && (
					<div className="flex flex-wrap items-center justify-between gap-3 border-t border-default-200 px-4 py-4 dark:border-default-700">
						<p className="m-0 text-sm text-default-500">
							{i18n.t("master.scheduleLogs.showing", {
								start: rangeStart,
								end: rangeEnd,
								total,
							})}
						</p>
						<div className="flex gap-2">
							<button
								type="button"
								className="btn btn-sm btn-outline-default"
								disabled={page <= 1}
								onClick={() => setPage((p) => Math.max(1, p - 1))}
							>
								{i18n.t("master.scheduleLogs.prev")}
							</button>
							<span className="flex items-center px-2 text-sm text-default-600">
								{page} / {totalPages}
							</span>
							<button
								type="button"
								className="btn btn-sm btn-outline-default"
								disabled={page >= totalPages}
								onClick={() => setPage((p) => p + 1)}
							>
								{i18n.t("master.scheduleLogs.next")}
							</button>
						</div>
					</div>
				)}
			</div>

			{trackScheduleId > 0 && (
				<TrackDrawer scheduleId={trackScheduleId} onClose={closeTrack} />
			)}
		</div>
	);
}
